import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import AppFooter from './AppFooter';
import './FAQ.css';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    {
      question: "What is GallopAI?",
      answer: "GallopAI is an automated horse racing betting system that uses artificial intelligence to analyse races and place bets on your behalf through your Betfair account. The system evaluates horses, jockeys, trainers, and other factors to make informed betting decisions."
    },
    {
      question: "How does the automated betting system work?",
      answer: "Our AI system analyses race data including horse form, jockey performance, trainer statistics, course conditions, and other relevant factors. Based on this analysis, it automatically places bets on your Betfair account for races that meet our selection criteria. You can enable AI Mode to have bets placed automatically from 11:00 AM (UK Time) each day."
    },
    {
      question: "Do I need a Betfair account?",
      answer: "Yes, you need a valid Betfair Exchange account to use GallopAI. The system connects to your Betfair account via their API to place bets automatically. You must be at least 18 years old and legally permitted to use gambling services in your jurisdiction."
    },
    {
      question: "How do I connect my Betfair account?",
      answer: "You can connect your Betfair account from the Dashboard page. You'll need to authorise GallopAI to access your Betfair account through Betfair's API. This is a secure process that allows the system to place bets on your behalf."
    },
    {
      question: "What is AI Mode?",
      answer: "AI Mode is an optional feature that automatically places bets on all daily picks starting from 11:00 AM (UK Time). When enabled, the system will calculate stakes using your stake per point setting and place bets without requiring manual action each day. Please note, you have to connect your Betfair account in order to enable AI Mode."
    },
    {
      question: "How are stakes calculated?",
      answer: "Stakes are calculated by multiplying your stake per point (set in Settings) by the suggested stake for each race. For example, if your stake per point is £10 and a race has a suggested stake of 1.75 points, the bet stake would be £17.50."
    },
    {
      question: "Can I cancel my subscription?",
      answer: "Yes. During a free trial, open Settings and choose Cancel trial — your access continues until the trial end date and you will not be charged. On a paid subscription, use Manage billing in Settings to cancel through Stripe; cancellation takes effect at the end of the current billing period."
    },
    {
      question: "I paid at Stripe but never finished registering. What do I do?",
      answer: "Open the Resume signup page, enter the same email you used at Stripe checkout, and follow the link back to create your GallopAI account. Use that exact email when you choose your password so we can link your subscription."
    },
    {
      question: "What is your refund policy?",
      answer: "We offer a 14-day money back guarantee for new customers. If you're unhappy with the service within 14 days of signing up, you can request a full refund. Please note that new customers are entitled to ONE refund or trial. For more details, please see our Refund Policy."
    },
    {
      question: "What happens if a bet is unmatched?",
      answer: "In AI mode, if a bet cannot be matched on Betfair (e.g., due to odds changes or insufficient liquidity), the system will automatically retry up to 3 times. If still unmatched, the bot will try again to place the bet 5 minutes later. This will continue in 5 minute increments up until the race start time to offer the best chance to securing enough liquidity to match the bet."
    },
    {
      question: "How do I view my betting history?",
      answer: "You can view your betting history in two places: the 'Performance' page shows all your settled bets with profit/loss calculations and charts over time. The 'Today's Bets' page lists every bet you placed since midnight UTC today—pending, unmatched, won, lost, or void—so you can review the current day's activity in one place. For older settled results by race, use 'Results History'."
    },
    {
      question: "Is my data secure?",
      answer: "Yes, we take data security seriously. Your Betfair credentials are encrypted and stored securely. We implement appropriate technical and organisational measures to protect your personal data. For more information, please see our Privacy Policy."
    },
    {
      question: "What if I have technical issues?",
      answer: "If you experience any technical issues, please contact our support team through the Contact page. We aim to respond to all support requests within 1 business day."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const renderAnswerWithLinks = (answer: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    let remaining = answer;
    let key = 0;

    // Process in order: Refund Policy, Privacy Policy, Contact page
    // Use a more robust approach with word boundaries
    const patterns = [
      {
        regex: /\bResume signup page\b/gi,
        link: '/resume-signup',
        text: 'Resume signup page'
      },
      {
        regex: /\bRefund Policy\b/gi,
        link: '/refund-policy',
        text: 'Refund Policy'
      },
      {
        regex: /\bPrivacy Policy\b/gi,
        link: '/privacy-policy',
        text: 'Privacy Policy'
      },
      {
        regex: /\bContact page\b/gi,
        link: '/contact',
        text: 'Contact page'
      }
    ];

    // Find all matches with their positions
    const matches: Array<{ start: number; end: number; link: string; text: string }> = [];
    
    patterns.forEach(pattern => {
      let match;
      const regex = new RegExp(pattern.regex.source, pattern.regex.flags);
      while ((match = regex.exec(remaining)) !== null) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length,
          link: pattern.link,
          text: match[0]
        });
      }
    });

    // Sort matches by position
    matches.sort((a, b) => a.start - b.start);

    // Build the result by processing text segments and links
    let lastIndex = 0;
    matches.forEach(match => {
      // Add text before the match
      if (match.start > lastIndex) {
        parts.push(<span key={`text-${key++}`}>{remaining.substring(lastIndex, match.start)}</span>);
      }
      
      // Add the link
      parts.push(
        <Link
          key={`link-${key++}`}
          to={match.link}
          target="_blank"
          rel="noopener noreferrer"
          className="faq-link"
        >
          {match.text}
        </Link>
      );
      
      lastIndex = match.end;
    });

    // Add remaining text after last match
    if (lastIndex < remaining.length) {
      parts.push(<span key={`text-${key++}`}>{remaining.substring(lastIndex)}</span>);
    }

    // If no matches, return the original text
    return parts.length > 0 ? parts : <span>{answer}</span>;
  };

  return (
    <div className="faq-page">
      <div className="faq-header">
        <img src="/white_wide_logo_noBG.png" alt="GallopAI" className="faq-logo" />
        <h1 className="faq-title">Frequently Asked Questions</h1>
        <p className="faq-subtitle">Find answers to common questions about GallopAI</p>
        <p className="faq-guide-pitch">
          For step-by-step training on each area of the app, open the{' '}
          <Link to="/guide/getting-started" className="faq-link">
            user guide
          </Link>
          .
        </p>
      </div>
      
      <div className="faq-content">
        <div className="faq-card">
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className={`faq-item ${openIndex === index ? 'open' : ''}`}>
                <button
                  className="faq-question"
                  onClick={() => toggleFAQ(index)}
                  aria-expanded={openIndex === index}
                >
                  <span>{faq.question}</span>
                  <ChevronDown 
                    className={`faq-icon ${openIndex === index ? 'open' : ''}`}
                    size={20}
                  />
                </button>
                <div className="faq-answer">
                  <p>{renderAnswerWithLinks(faq.answer)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="faq-contact-section">
            <h3 className="faq-contact-title">Still have questions?</h3>
            <p className="faq-contact-text">
              If you couldn't find the answer you're looking for, our support team is here to help.
            </p>
            <Link to="/contact" target="_blank" rel="noopener noreferrer" className="faq-contact-button">
              Contact Us
            </Link>
          </div>
        </div>
      </div>

      <AppFooter />
    </div>
  );
};

export default FAQ;

